import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import { memoize } from '@wp-g2/utils';
import { getPropValue } from './get-prop-value';
import { hasVariable, isCustomProperty } from './utils';
/**
 * Interprets and retrieves the CSS fallback value of a declaration rule.
 *
 * @param {string} declaration A CSS declaration rule to parse.
 * @param {object} rootStore A store for CSS root variables.
 * @returns {string | undefined} A CSS declaration rule with a fallback (if applicable).
 */

export function getFallbackDeclaration(declaration, rootStore) {
  if (!hasVariable(declaration) && !isCustomProperty(declaration)) return undefined;

  var _getPropValue = getPropValue(declaration, rootStore),
      _getPropValue2 = _slicedToArray(_getPropValue, 2),
      prop = _getPropValue2[0],
      value = _getPropValue2[1];

  return value ? [prop, value].join(':') : undefined;
}
/**
 * Parses the incoming content from stylis to add fallback CSS values for
 * variables.
 *
 * @param {string} content Stylis content to parse.
 * @param {object} rootStore A store for CSS root variables.
 * @return {string | undefined} The transformed content with CSS variable fallbacks.
 */

export function baseTransformContent(content, rootStore) {
  /*
   * Attempts to deconstruct the content to retrieve prop/value
   * CSS declaration pairs.
   *
   * Before:
   * 'background-color:var(--bg, black); font-size:14px;'
   *
   * After:
   * ['background-color:var(--bg, black)', ' font-size:14px']
   */
  var declarations = content.split(';').filter(Boolean);
  var didTransform = false;
  /*
   * With the declaration collection, we'll iterate over every declaration
   * to provide fallbacks (if applicable.)
   */

  var parsed = declarations.reduce(function (
  /** @type {string[]} */
  styles,
  /** @type {string} */
  declaration) {
    // If no CSS variable is used, we return the declaration untouched.
    if (!hasVariable(declaration)) {
      return [].concat(styles, [declaration]);
    } // Retrieve the fallback a CSS variable is used in this declaration.


    var fallback = getFallbackDeclaration(declaration, rootStore);
    /*
     * Prepend the fallback in our styles set.
     *
     * Before:
     * [
     * 	 ...styles,
     *   'background-color:var(--bg, black);'
     * ]
     *
     * After:
     * [
     * 	 ...styles,
     *   'background:black;',
     *   'background-color:var(--bg, black);'
     * ]
     */

    if (fallback) {
      didTransform = true;
      return [].concat(styles, [fallback, declaration]);
    }

    return [].concat(styles, [declaration]);
  }, []);
  /*
   * We'll rejoin our declarations with a ; separator.
   * Note: We need to add a ; at the end for stylis to interpret correctly.
   */

  var result = parsed.join(';').concat(';'); // We only want to return a value if we're able to locate a fallback value.

  return didTransform ? result : undefined;
}
export var transformContent = memoize(baseTransformContent);